import {
	Avatar,
	Checkbox,
	ListItem,
	ListItemAvatar,
	ListItemButton,
	Radio,
	Typography,
} from '@mui/material';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import CheckBoxIcon from '../../../assets/check-box.svg';
import CheckBoxCheckedIcon from '../../../assets/check-box-checked.svg';
import RadioButtonIcon from '../../../assets/radio-button.svg';
import RadioButtonSelectedIcon from '../../../assets/radio-button-selected.svg';
import { borderRadius, boxShadows, palette } from '../../../assets/styles/themes/tokens';
import createNonPropagatingEventHandler from '../../../utils/createNonPropagatingEventHandler';

interface OwnProps {
	label: string;
	avatar?: React.ReactNode;
	isSelected: boolean;
	isDisabled?: boolean;
	isRadioButton?: boolean;
	onClick: () => void;
	sxProps?: React.CSSProperties;
}

const OnboardingOptionItem: React.FC<OwnProps> = (props) => {
	const { t } = useTranslation();
	const { label, avatar, isSelected, isDisabled, isRadioButton, onClick, sxProps } = props;

	const style = {
		borderRadius: borderRadius.lg,
		border: `1px solid ${isSelected ? palette.primary.main : palette.elevation.outlined}`,
		backgroundColor: palette.background.paperElevation2,
		'&:hover': {
			boxShadow: isSelected ? 'none' : boxShadows.elevation3,
		},
	};

	const handleClick = createNonPropagatingEventHandler(() => {
		if (!isDisabled) {
			onClick();
		}
	});

	return (
		<ListItem
			data-testid={`onboarding-option-${label}`}
			key={label}
			onClick={handleClick}
			secondaryAction={
				isRadioButton ? (
					<Radio
						name={`radio-button-${label}`}
						checked={isSelected}
						onClick={handleClick}
						icon={<RadioButtonIcon />}
						checkedIcon={<RadioButtonSelectedIcon />}
					/>
				) : (
					<Checkbox
						onClick={handleClick}
						checked={isSelected}
						disabled={isDisabled}
						icon={<CheckBoxIcon />}
						checkedIcon={<CheckBoxCheckedIcon />}
					/>
				)
			}
			sx={{
				...style,
				...sxProps,
			}}
		>
			<ListItemButton
				disabled={isDisabled}
				disableRipple
				sx={{
					paddingLeft: 0,
					'&:hover': {
						backgroundColor: 'transparent',
					},
				}}
			>
				{avatar && (
					<ListItemAvatar>
						<Avatar sx={{ background: 'transparent' }}>{avatar}</Avatar>
					</ListItemAvatar>
				)}
				<Typography
					variant="h6"
					sx={{
						color: palette.text.primary,
						fontWeight: 500,
						textAlign: 'center',
					}}
				>
					{t(label)}
				</Typography>
			</ListItemButton>
		</ListItem>
	);
};

export default OnboardingOptionItem;
